import Colors from '@utils/Colors.js'

// Tokens shared by all UI components: shadows, corner radii, the stacking order, the hover overlay, the keyboard focus
// ring and the status colours. Tokens of one group of components (fields, table, buttons) live in that folder's
// styles.ts and are built from these where they overlap. Change a value here instead of in a component.

export const shadows = {
    // Cards, panels and raised buttons (solid, outline)
    raised: '0px 2px 4px 0px rgba(0, 0, 0, 0.25)',
    // Things that float over the page without a backdrop: toasts and tooltips
    floating: '0 1px 2px 0 rgba(60,64,67,.3), 0 2px 6px 2px rgba(60,64,67,.15)',
    // The panel of a Popover (menus, option lists, calendars)
    popover: `0 3px 6px ${Colors.border}, 0 3px 6px rgba(0,0,0,0.23)`
}

export const radius = {
    // Buttons, cards, alerts, modals
    default: 4,
    // Badges
    pill: 24,
    // Icon buttons and other round controls
    round: '50%'
}

// Stacking order of everything that lies over the page, from low to high. A Modal opened while others are open lies
// one higher than the one under it (modal + 1, + 2...), so a Popup opened from a Modal covers it. Popover sits
// above the map controls (99999999) and above any modal it is opened from; tooltips sit above everything.
export const zIndex = {
    // The top bar and the side nav, above the page
    nav: 405,
    toast: 1000,
    // The first Modal, a Modal opened from it gets modal + 1 and so on
    modal: 1030,
    mapControls: 99999999,
    popover: 100000000,
    // The largest z-index browsers support
    tooltip: 2147483647
}

// Background laid over a control or row while hovered (or focused), so it works on any background colour
export const hoverOverlay = {
    // Icon buttons and choice controls (Check, RadioButton, Toggle)
    default: 'rgba(0, 0, 0, 0.05)',
    // Large areas: table rows and list items
    subtle: 'rgba(0, 0, 0, 0.02)'
}

// Ring around a control focused with the keyboard (:focus-visible, see ensureFocusStyles below). Not shown for mouse or touch
// focus. Blue, so it stands out next to the grey buttons and on the dark nav bar alike.
export const focusRing = {
    width: 2,
    offset: 2,
    color: Colors.infoBright
}

// Inline styles can't target :focus-visible, so the focus ring rule is injected once as a <style> element in the head
// instead of living in a stylesheet, like forms/fieldStyles.ts. Built from focusRing above.

/**
 * Class of the controls that show the focus ring (buttons, tabs, links). They get no outline for mouse or touch focus
 * and the ring for keyboard focus, so they must not set outline in their inline style.
 */
export const focusableClass = 'ui-focusable'

const styleElementId = 'veloyd-focus-styles'

const css = [
    `.${focusableClass} { outline: none; }`,
    `.${focusableClass}:focus-visible { outline: ${focusRing.width}px solid ${focusRing.color}; outline-offset: ${focusRing.offset}px; }`
].join('\n')

/** Adds the focus rules to the document once, safe to call any number of times and without a document (tests, SSR) */
export function ensureFocusStyles() {
    if (typeof document === 'undefined' || document.getElementById(styleElementId)) {
        return
    }

    const style = document.createElement('style')
    style.id = styleElementId
    style.textContent = css
    document.head.appendChild(style)
}

export interface StatusColors {
    /** Background of an Alert */
    pale: string
    /** Background of a Badge */
    light: string
    /** Icons and status dots */
    bright: string
    /** Solid backgrounds with white text (Button variant 'error'), text on a pale background */
    dark: string
}

// One set of colours per status, shared by Alert, Badge, Toast and the 'error' Button
export const statusColors: Record<'success' | 'info' | 'warning' | 'error' | 'neutral', StatusColors> = {
    success: {pale: Colors.successPale, light: Colors.successLight, bright: Colors.successBright, dark: Colors.successDark},
    info: {pale: Colors.infoPale, light: Colors.infoLight, bright: Colors.infoBright, dark: Colors.infoDark},
    warning: {pale: Colors.warningPale, light: Colors.warningLight, bright: Colors.warningBright, dark: Colors.warningDark},
    error: {pale: Colors.errorPale, light: Colors.errorLight, bright: Colors.errorBright, dark: Colors.errorDark},
    neutral: {pale: Colors.grey20, light: Colors.grey30, bright: Colors.grey40, dark: Colors.grey60}
}
